import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";
import AnimatedResultWrapper from "../../loaders/AnimatedResultWrapper";
import CopyButton from "../../ui/CopyButton";
import MotionButton from "../../ui/MotionButton";
import MotionSurface from "../../../motion/studio/MotionSurface";
import ResultReveal from "../../../motion/studio/ResultReveal";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const HookTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const templateOptions = useMemo(
    () => [
      {
        value: "curiosity",
        label: t("hookTool.templates.curiosity.label", "Curiosity"),
        description: t(
          "hookTool.templates.curiosity.description",
          "Creates intrigue and makes people want the next line."
        ),
      },
      {
        value: "authority",
        label: t("hookTool.templates.authority.label", "Authority"),
        description: t(
          "hookTool.templates.authority.description",
          "Feels confident, informed, and expert-led."
        ),
      },
      {
        value: "problem",
        label: t("hookTool.templates.problem.label", "Problem"),
        description: t(
          "hookTool.templates.problem.description",
          "Calls out pain points, bottlenecks, or mistakes fast."
        ),
      },
      {
        value: "benefit",
        label: t("hookTool.templates.benefit.label", "Benefit"),
        description: t(
          "hookTool.templates.benefit.description",
          "Leads with payoff, result, or transformation."
        ),
      },
      {
        value: "controversy",
        label: t("hookTool.templates.controversy.label", "Controversy"),
        description: t(
          "hookTool.templates.controversy.description",
          "Uses tension, contrast, or bold disagreement."
        ),
      },
      {
        value: "story",
        label: t("hookTool.templates.story.label", "Story"),
        description: t(
          "hookTool.templates.story.description",
          "Feels personal, human, and emotionally grounded."
        ),
      },
    ],
    [t]
  );

  const familyLabel = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return t("hookTool.familyFallback", "Hook");
    return raw
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const scoreTone = (score) => {
    const safe = Number(score) || 0;

    if (safe >= 85) {
      return {
        bar: "#16a34a",
        pillBg: "rgba(22,163,74,0.14)",
        pillText: "#16a34a",
      };
    }

    if (safe >= 70) {
      return {
        bar: "#2563eb",
        pillBg: "rgba(37,99,235,0.14)",
        pillText: "#2563eb",
      };
    }

    return {
      bar: "#f59e0b",
      pillBg: "rgba(245,158,11,0.16)",
      pillText: "#b45309",
    };
  };

  const [template, setTemplate] = useState("curiosity");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [offer, setOffer] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [angle, setAngle] = useState("");
  const [callToAction, setCallToAction] = useState("");
  const [keywords, setKeywords] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [avoidPhrases, setAvoidPhrases] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [hooks, setHooks] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedHookIndexes, setSavedHookIndexes] = useState([]);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      panelBgStrong: isDark ? "rgba(30,41,59,0.78)" : "#f8fafc",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      chipBg: isDark ? "rgba(30,41,59,0.9)" : "#f1f5f9",
      chipText: isDark ? "#cbd5e1" : "#475569",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
      outputBg: isDark ? "rgba(37,99,235,0.10)" : "#eff6ff",
      outputBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
      outputText: isDark ? "#dbeafe" : "#1e3a8a",
      selectedTemplateBg: isDark
        ? "linear-gradient(135deg, rgba(37,99,235,0.16), rgba(59,130,246,0.12))"
        : "linear-gradient(135deg, #eff6ff, #f8fbff)",
      selectedTemplateBorder: isDark ? "#60a5fa" : "#3b82f6",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "86px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const generateHooks = async () => {
    if (!sharedTopic.trim()) {
      setError(t("hookTool.messages.topicRequired", "Please enter a topic first."));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const payload = {
        topic: sharedTopic.trim(),
        platform: sharedPlatform,
        template,
        language: getPreferredLanguage(),
        audience,
        goal,
        offer,
        pain_point: painPoint,
        desired_outcome: desiredOutcome,
        angle,
        call_to_action: callToAction,
        keywords,
        brand_voice: brandVoice,
        avoid_phrases: avoidPhrases,
        extra_context: extraContext,
      };

      let response;

      try {
        response = await API.post("/hooks/generate", payload);
      } catch (firstErr) {
        if (firstErr?.response?.status !== 404) {
          throw firstErr;
        }

        response = await API.post("/hooks/", payload);
      }

      const responseData = response?.data || {};
      const rawHooks = Array.isArray(responseData?.hooks)
        ? responseData.hooks
        : responseData?.hook
          ? [responseData.hook]
          : Array.isArray(responseData)
            ? responseData
            : [];

      setSavedHookIndexes([]);
      setHooks(rawHooks);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate hooks:", err);
      setError(
        err?.response?.data?.detail ||
          t("hookTool.messages.generateFailed", "Failed to generate hooks.")
      );
    } finally {
      setLoading(false);
    }
  };


  const saveHook = async (hook, index) => {
    try {
      const hookText =
        typeof hook === "string"
          ? hook
          : hook?.text || hook?.content || hook?.hook || "";

      const hookScore =
        typeof hook === "string"
          ? 0
          : Number(hook?.score) || 0;

      await API.post("/hooks/save", {
        topic: sharedTopic,
        text: hookText,
        score: hookScore,
        platform: sharedPlatform,
      });

      setSavedHookIndexes((prev) => [...prev, index]);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to save hook:", err);

      setError(
        err?.response?.data?.detail ||
        "Failed to save hook."
      );
    }
  };

  const copyHook = async (hook, id) => {
    const hookText =
      typeof hook === "string"
        ? hook
        : hook?.text || hook?.content || hook?.hook || "";

    try {
      await navigator.clipboard.writeText(hookText);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy hook:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const tone = scoreTone(safe);

    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: tone.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("hookTool.heroBadge", "Output-first tool")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("hookTool.title", "Hooks")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                "hookTool.description",
                "Use this tool when you want <strong>ready-to-use opening lines</strong>. Start with your topic, choose a hook style, and Hookora will generate direct hooks you can use for captions, videos, carousels, titles, intros, and post openings."
              ),
            }}
          />
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("hookTool.fields.topic", "Topic")}
              <FieldHint
                text={t(
                  "hookTool.hints.topic",
                  "The specific content idea you want hooks for. Clear topics produce stronger, more usable hooks."
                )}
              />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t(
                "hookTool.placeholders.topic",
                "What do you want the hook to be about?"
              )}
              style={{ ...textareaStyle, minHeight: "92px" }}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("hookTool.fields.hookStyle", "Hook Style")}
              <FieldHint
                text={t(
                  "hookTool.hints.hookStyle",
                  "Choose the kind of hook you want Hookora to prioritize: curiosity, authority, problem-led, benefit-led, controversy, or story."
                )}
              />
            </label>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "10px",
              }}
            >
              {templateOptions.map((item) => {
                const active = template === item.value;

                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setTemplate(item.value)}
                    style={{
                      padding: "12px",
                      borderRadius: "14px",
                      border: `1px solid ${
                        active ? colors.selectedTemplateBorder : colors.border
                      }`,
                      background: active ? colors.selectedTemplateBg : colors.panelBg,
                      textAlign: "left",
                      cursor: "pointer",
                      color: colors.text,
                    }}
                  >
                    <div style={{ fontWeight: 800, marginBottom: "4px" }}>{item.label}</div>
                    <div style={{ fontSize: "12px", lineHeight: "1.55", color: colors.textMuted }}>
                      {item.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label style={labelStyle}>
                {t("hookTool.fields.platform", "Platform")}
                <FieldHint
                  text={t(
                    "hookTool.hints.platform",
                    "Choose where the hook will be used. Platform affects rhythm, directness, and stop-power."
                  )}
                />
              </label>
              <select
                value={sharedPlatform}
                onChange={(e) => setSharedPlatform(e.target.value)}
                style={inputStyle}
              >
                {[
                  "YouTube",
                  "YouTube Shorts",
                  "Instagram",
                  "Instagram Reels",
                  "TikTok",
                  "X",
                  "LinkedIn",
                  "Facebook",
                ].map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                {t("hookTool.fields.audience", "Audience")}
                <FieldHint
                  text={t(
                    "hookTool.hints.audience",
                    "Who the hook is trying to stop. Example: founders, creators, coaches, small business owners, students."
                  )}
                />
              </label>
              <input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder={t("hookTool.placeholders.audience", "Audience")}
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("hookTool.contextSectionTitle", "Hook Context")}
           </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("hookTool.fields.goal", "Goal")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.goal",
                      "What the hook should help achieve. Example: more clicks, better retention, stronger comments, more signups."
                    )}
                  />
                </label>
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("hookTool.placeholders.goal", "Goal")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookTool.fields.offer", "Offer")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.offer",
                      "The product, service, lesson, or value sitting behind the hook."
                    )}
                  />
                </label>
                <input
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  placeholder={t("hookTool.placeholders.offer", "Offer or product")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookTool.fields.painPoint", "Pain Point")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.painPoint",
                      "The exact frustration, challenge, or mistake the hook should tap into."
                    )}
                  />
                </label>
                <input
                  value={painPoint}
                  onChange={(e) => setPainPoint(e.target.value)}
                  placeholder={t("hookTool.placeholders.painPoint", "Pain point")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookTool.fields.desiredOutcome", "Desired Outcome")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.desiredOutcome",
                      "The result your audience wants. Example: more reach, more sales, better positioning, stronger content."
                    )}
                  />
                </label>
                <input
                  value={desiredOutcome}
                  onChange={(e) => setDesiredOutcome(e.target.value)}
                  placeholder={t("hookTool.placeholders.desiredOutcome", "Desired outcome")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookTool.fields.angle", "Angle")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.angle",
                      "Optional direction to bias the hook. Example: contrarian, hidden truth, mistake-led, premium, emotional."
                    )}
                  />
                </label>
                <input
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  placeholder={t("hookTool.placeholders.angle", "Angle")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookTool.fields.ctaDirection", "CTA Direction")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.ctaDirection",
                      "Optional action you want the content to lead toward. Example: comment, save, click, DM, sign up."
                    )}
                  />
                </label>
                <input
                  value={callToAction}
                  onChange={(e) => setCallToAction(e.target.value)}
                  placeholder={t("hookTool.placeholders.ctaDirection", "CTA direction")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookTool.fields.keywords", "Keywords")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.keywords",
                      "Important words or phrases that should influence the hook."
                    )}
                  />
                </label>
                <input
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder={t("hookTool.placeholders.keywords", "Keywords")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookTool.fields.brandVoice", "Brand Voice")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.brandVoice",
                      "How the hook should feel. Example: bold, premium, warm, sharp, playful, direct."
                    )}
                  />
                </label>
                <input
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  placeholder={t("hookTool.placeholders.brandVoice", "Brand voice")}
                  style={inputStyle}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>
                  {t("hookTool.fields.avoidPhrases", "Avoid Phrases")}
                  <FieldHint
                    text={t(
                      "hookTool.hints.avoidPhrases",
                      "Words, clichés, or overused expressions you do not want Hookora to use."
                    )}
                  />
                </label>
                <input
                  value={avoidPhrases}
                  onChange={(e) => setAvoidPhrases(e.target.value)}
                  placeholder={t("hookTool.placeholders.avoidPhrases", "Avoid phrases")}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("hookTool.fields.extraContext", "Extra Context")}
                <FieldHint
                  text={t(
                    "hookTool.hints.extraContext",
                    "Add proof, campaign notes, objections, examples, positioning details, or supporting context to improve the final hooks."
                  )}
                />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t(
                  "hookTool.placeholders.extraContext",
                  "Extra context, positioning note, proof, examples, or campaign details"
                )}
                style={{ ...textareaStyle, minHeight: "96px" }}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {typeof error === "string"
  ? error
  : error?.message || "Something went wrong"}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateHooks}
              disabled={loading}
              style={{
                padding: "13px 16px",
                borderRadius: "12px",
                border: "none",
                background: colors.buttonBg,
                color: colors.buttonText,
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading
                ? t("hookTool.actions.generating", "Generating...")
                : t("hookTool.actions.generate", "Generate Hooks")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={() => setHooks([])}
              style={{
                padding: "13px 16px",
                borderRadius: "12px",
                border: `1px solid ${colors.borderStrong}`,
                background: colors.secondaryButtonBg,
                color: colors.secondaryButtonText,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {t("hookTool.actions.clearResults", "Clear Results")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!hooks.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                "hookTool.messages.readyToUse",
                "These are <strong>ready-to-use hooks</strong>. Pick the strongest one and use it directly as your opening line, title opener, video intro, caption opener, or first carousel slide."
              ),
            }}
          />
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
  {loading ? (
    <ToolResultSkeleton
      isDark={isDark}
      count={3}
    />
  ) : (
    hooks.map((hook, index) => {
          const hookText =
            typeof hook === "string"
              ? hook
              : hook?.text || hook?.content || hook?.hook || "";

          const hookScore =
            typeof hook === "string" ? 0 : Number(hook?.score) || 0;

          const hookFamily =
            typeof hook === "string" ? "" : hook?.family || "";

          const tone = scoreTone(hookScore);

      return (
  <ResultCard
  key={`${hookText}-${index}`}
  colors={colors}
  index={index}
  >
    
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
          alignItems: "flex-start",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {hookFamily ? (
            <span
              style={{
                background: colors.outputBg,
                color: colors.outputText,
                border: `1px solid ${colors.outputBorder}`,
                borderRadius: "999px",
                padding: "6px 10px",
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              }}
            >
              {familyLabel(hookFamily)}
            </span>
          ) : null}

          {hookScore ? (
            <span
              style={{
                background: tone.pillBg,
                color: tone.pillText,
                borderRadius: "999px",
                padding: "6px 10px",
                fontSize: "12px",
                fontWeight: 800,
              }}
            >
              {t("hookTool.messages.score", "Score")}: {hookScore}
            </span>
          ) : null}
        </div>
      </div>

      <div
        style={{
          color: colors.text,
          fontWeight: 800,
          fontSize: "22px",
          lineHeight: "1.55",
          marginBottom: hookScore ? "12px" : "0",
          whiteSpace: "pre-wrap",
        }}
      >
        {hookText}
      </div>

      {hookScore ? renderScoreBar(hookScore) : null}

      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginTop: "14px",
        }}
      >
        <CopyButton
          copied={copiedId === index}
          onClick={() => copyHook(hook, index)}
          style={{
  padding: "10px 14px",
  borderRadius: "12px",
  border: `1px solid ${colors.borderStrong}`,
  background: isDark
    ? "rgba(30,41,59,0.82)"
    : "rgba(255,255,255,0.92)",
  color: colors.text,
  cursor: "pointer",
  fontWeight: 700,
  backdropFilter: "blur(12px)",
  boxShadow: isDark
    ? "0 8px 22px rgba(2,6,23,0.22)"
    : "0 8px 20px rgba(15,23,42,0.08)",
}}
        >
          {copiedId === index
            ? t("hookTool.actions.copied", "Copied")
            : t("hookTool.actions.copyHook", "Copy Hook")}
        </CopyButton>

        <MotionButton
          onClick={() => saveHook(hook, index)}
          disabled={savedHookIndexes.includes(index)}
          style={{
            padding: "10px 14px",
            borderRadius: "12px",
            border: `1px solid ${colors.borderStrong}`,
            background: colors.buttonBg,
            color: colors.buttonText,
            cursor: "pointer",
            fontWeight: 700,
            opacity: savedHookIndexes.includes(index) ? 0.6 : 1,
          }}
        >
          {savedHookIndexes.includes(index)
            ? "Saved"
            : "Save Hook"}
        </MotionButton>
      </div>
    </ResultCard>
);
          
                })
      )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default HookTool;